"use client"

import { useEffect } from "react"
import { createPortal } from "react-dom"
import { EntityTypeProvider } from "@/contexts/entity-type-context"
import { WorkflowProvider, useWorkflow } from "@/context/workflow-context-simple"
import { WorkflowGrid } from "@/components/workflow-grid"
import { ViewSelector } from "@/components/view-selector"
import { useClientRequestConfig } from "@/hooks/client-request/use-client-request-config"
import { useRequestsTabHeaderSlot, useRequestsTabOpenViewManager } from "@/contexts/requests-tab-header-slot-context"
import { Button } from "@/components/ui/button"
import { UserPlus } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import { useParentApp } from "@/contexts/parent-app-context"
import { sendMessageToParent } from "@/lib/app-messaging"
import { buildOnboardingPath } from "@/lib/client-request-action-utils"
import type { WorkflowStage } from "@/types/workflow"

/**
 * Renders the ClientRequest tab via the shared <WorkflowGrid>.
 *
 * Replaces the bespoke <ClientRequestsGrid> in workflow-hub.tsx. The structural
 * shape mirrors {@link VmsSubTabContent}: EntityTypeProvider (so child code can
 * branch on entityType via useEntityType()) wraps a WorkflowProvider seeded
 * with the configuration the hook already fetched (so the provider's own
 * mount-time fetch coalesces against ours rather than re-requesting). The
 * WorkflowGrid then drives columns, filters, actions, and the kebab from the
 * seeded WorkflowHubConfigurationDto on the backend.
 *
 * Pin dock and Jump-to-Column are hidden — ClientRequest doesn't have a
 * column-pinning use case today (matches the VMS treatment). If a tenant
 * wants pinning later, drop the hidePinDock/hideJumpToColumn props and the
 * dock surfaces automatically off the seeded field configs.
 */
export function ClientRequestSubTabContent() {
  const { config, isLoading, error } = useClientRequestConfig()
  const { t } = useTranslation()
  const { parentApp } = useParentApp()

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    )
  }

  const stage = config?.stage
  if (!config || !stage) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-400">No stage configured for Client Requests</p>
      </div>
    )
  }

  // "New Onboarding Request" — restored after the migration from the bespoke
  // <ClientRequestsGrid> to the shared <WorkflowGrid> dropped it. Injected via
  // headerExtraControls (same mechanism ATS uses for "Create New ATS"). Navigates
  // the host shell to the create-onboarding form; buildOnboardingPath resolves the
  // FBO vs client-portal route off parentApp. CR-scoped by construction (this
  // wrapper only renders for ClientRequest).
  const headerControls = (
    <Button
      variant="elliptical"
      onClick={() => sendMessageToParent({ action: 'NAVIGATE', route: buildOnboardingPath(parentApp, 'create') })}
      className="flex items-center gap-2"
    >
      <UserPlus size={16} />
      {t('newOnboardingRequest')}
    </Button>
  )

  return (
    <EntityTypeProvider value="ClientRequest">
      {/* Hand the configuration that useClientRequestConfig already fetched
          down to the provider — without this, the provider's mount effect
          would re-issue the same /configurations?entityType=ClientRequest
          request (coalesceWorkflowFetch only catches concurrent calls, and
          this provider only mounts AFTER the hook settles). */}
      <WorkflowProvider initialConfiguration={config.configuration}>
        {/* The outer h-full + flex-col layer mirrors VmsPlacementsTab's
            container. Without h-full the inner WorkflowGrid's
            getBoundingClientRect().height measures as ~0 (the flex-1 has
            nothing to stretch against), so the grid's auto-rows-per-page
            heuristic falls back to its minRowsPerPage = 5 floor. With
            h-full, the grid measures the actual tab content area and
            picks a sensible row count. */}
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            <WorkflowGrid
              stage={stage.stageCode as WorkflowStage}
              hidePinDock
              hideJumpToColumn
              headerExtraControls={headerControls}
            />
          </div>
        </div>
        {/* ViewSelector portals into the workflow-hub top-right slot so it
            lands next to the search/kebab — same placement WorkOrder uses.
            It must mount under THIS WorkflowProvider (not at hub level)
            so useWorkflow() resolves to the ClientRequest context; portal
            decouples React tree position from rendered output location.
            Slot is null until the hub's placeholder div mounts and the ref
            callback fires — the conditional ensures we don't try to portal
            into null. */}
        <ViewSelectorHeaderPortal />
      </WorkflowProvider>
    </EntityTypeProvider>
  )
}

/**
 * Bridge component that lives INSIDE the WorkflowProvider so its rendered
 * <ViewSelector /> can read useWorkflow(), but uses createPortal to land
 * in the workflow-hub-level header slot. Kept tiny + separate from the
 * tab content so the portal's lifecycle is clear (mount → register
 * portal; unmount on tab switch → portal disappears, slot becomes empty).
 */
function ViewSelectorHeaderPortal() {
  const slot = useRequestsTabHeaderSlot()
  // Without this the "View Designer" entry never renders on this tab (ViewSelector gates it on the
  // prop). The designer itself is hub-level and scopes itself to the active tab's entity type.
  const onOpenViewManager = useRequestsTabOpenViewManager()
  if (!slot) return null
  return createPortal(<ViewSelector onOpenViewManager={onOpenViewManager ?? undefined} />, slot)
}
